/* @jsx h */
import {
  defineComponent,
  provide,
  ref,
  inject,
  watch,
  h,
  type Ref,
  computed,
} from "vue";
import { useRoute } from "vue-router";
import { BottomSheet } from "@lemmonhq/uikit";

// Define the types for the sheet context
type SheetParams = Record<string, any>;

interface SheetContextType {
  currentSheetType: Ref<string | null>;
  currentSheetParams: Ref<SheetParams | null>;
  closeSheet: () => void;
}

// Key for provide/inject
const SheetContextKey = Symbol("SheetContext");

// SheetProvider Component
export const SheetProvider = defineComponent({
  setup(_, { slots }) {
    const route = useRoute();

    const currentSheetType = ref<string | null>(null);
    const currentSheetParams = ref<SheetParams | null>(null);

    const showSheet = computed(() => !!currentSheetType.value);

    const updateSheetFromRoute = () => {
      const { sheet_type, sheet_params } = route.query;

      const sheetType = typeof sheet_type === "string" ? sheet_type : null;
      const sheetParams =
        typeof sheet_params === "string"
          ? JSON.parse(decodeURIComponent(sheet_params))
          : null;

      if (
        sheetType &&
        (sheetType !== currentSheetType.value ||
          JSON.stringify(sheetParams) !==
            JSON.stringify(currentSheetParams.value))
      ) {
        currentSheetType.value = sheetType;
        currentSheetParams.value = sheetParams;
      } else if (!sheetType) {
        currentSheetType.value = null;
        currentSheetParams.value = null;
      }
    };

    watch(
      () => route.query,
      () => updateSheetFromRoute(),
      { immediate: true },
    );

    const closeSheet = () => {
      currentSheetType.value = null;
      currentSheetParams.value = null;
      // Optional: update route to remove query params
    };

    provide(SheetContextKey, {
      currentSheetType,
      currentSheetParams,
      closeSheet,
    });

    return () => (
      <>
        {slots.default?.()}
        {showSheet.value && (
          <BottomSheet
            isOpen={true}
            onClose={() => closeSheet()}
            title={currentSheetType.value as string}
          >
            <div>
              <h3 style={{ padding: "1rem", borderBottom: "1px solid #eee" }}>
                {currentSheetType.value}
              </h3>
              <pre style={{ padding: "1rem" }}>
                {JSON.stringify(currentSheetParams.value, null, 2)}
              </pre>
              <button style={{ margin: "1rem" }} onClick={closeSheet}>
                Close
              </button>
            </div>
          </BottomSheet>
        )}
      </>
    );
  },
});

// Hook to use Sheet
export function useSheet(): SheetContextType {
  const context = inject<SheetContextType>(SheetContextKey);
  if (!context) {
    throw new Error("useSheet must be used within a SheetProvider");
  }
  return context;
}
